import { lazy, useEffect, useMemo, type FC } from 'react';
import type { NotificationsParams } from 'src/apis/notification-service/types';
import { useNotificationFilterOptions } from 'src/apis/notification-service/use-notification-filter-options';
import { type NotificationServiceRequest } from 'src/apis/notification-service/use-notification-service-root';
import {
  useNotifications,
  type NotificationsRequest,
} from 'src/apis/notification-service/use-notifications';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import {
  useNotificationsPageParams,
  type NotificationsPageParams,
} from 'src/pages/your-timeline-notifications/overview/use-page-params';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(() => import('./page'));

export const NotificationsOverview: FC<{
  notificationService: Successful<NotificationServiceRequest>;
}> = ({ notificationService }) => {
  const pageParams = useNotificationsPageParams();
  if (!notificationService.response.data._links.getNotifications)
    throw new Forbidden();

  const params = useMemo(() => {
    return {
      lastSeenAt: pageParams.value.lastSeenAt,
      lastSeenId: pageParams.value.lastSeenId,
      size: pageParams.value.size,
      context: pageParams.value.context?.value ?? null,
      actorUserId: pageParams.value.actorUserId?.value,
      mainAggregateBusinessId: pageParams.value.mainAggregateBusinessId,
      counterpartyOrganisationId:
        pageParams.value.counterpartyOrganisationId?.value,
      notificationDateFrom: pageParams.value.notificationDateFrom,
      notificationDateTo: pageParams.value.notificationDateTo,
    } satisfies NotificationsParams;
  }, [pageParams.value]);

  const notifications = useNotifications();

  useEffect(() => {
    notifications.execute({ notificationService, params });
  }, [notifications, notificationService, params]);

  if (!notifications.response) {
    return <PageSpinner />;
  }

  return (
    <NotificationsOverviewPage
      notifications={notifications}
      notificationService={notificationService}
      pageParams={pageParams}
    />
  );
};

const NotificationsOverviewPage: FC<{
  notifications: Successful<NotificationsRequest>;
  notificationService: Successful<NotificationServiceRequest>;
  pageParams: NotificationsPageParams;
}> = ({ notifications, notificationService, pageParams }) => {
  const notificationsFilterOptions = useNotificationFilterOptions({
    notifications,
    context: pageParams.value.context?.value,
  });

  if (!notificationsFilterOptions.response) {
    return <PageSpinner />;
  }

  return (
    <Page
      notifications={notifications}
      notificationService={notificationService}
      notificationsFilterOptions={notificationsFilterOptions}
      pageParams={pageParams}
    />
  );
};
